// Auth service — mirrors mobile app useAuth pattern
// Uses Cloudflare Workers API for credits (same backend as mobile app)
import { authStore } from './auth.store.js';
import { supabaseAuth } from '@services/supabase/auth.js';
import { supabase } from '@services/supabase/client.js';
import { creditsApi } from '@services/api/endpoints/credits.js';
import { localStorageService } from '@services/storage/localStorage.js';
import { sessionStorageService } from '@services/storage/sessionStorage.js';
import { libraryStore } from '@features/home/library.store.js';
import { libraryService } from '@features/home/library.service.js';
import { profileStore } from '@features/profile/profile.store.js';
import { router } from '@lib/dom/router.js';
import { ROUTES, STORAGE_KEYS, BLOCKED_EMAIL_PATTERNS } from '@config/constants.js';

export const authService = {
  // ───────── Initialization ─────────

  /**
   * Initialize auth — decide auth state from Supabase session only.
   * Sets isInitialized=true when done (unblocks AuthGate).
   */
  async init() {
    try {
      authStore.setKey('isLoading', true);

      // Decide login state during the AuthGate loading phase via Supabase only.
      try {
        const session = await supabaseAuth.getSession();
        if (session) {
          const user = await supabaseAuth.getUser();

          // Block if Supabase returns a demo/test/fake account
          if (_isBlockedEmail(user.email)) {
            console.warn('[authService] Blocked account detected from Supabase, signing out:', user.email);
            _clearLocalCaches();
            _clearState();
            await _clearSupabaseSession();
            authStore.setKey('isLoading', false);
            return; // skip everything, will hit finally → isInitialized = true
          }

          const userData = _buildUser(user);
          const sessionData = _buildSession(session);

          _primeLibraryInitialLoading();

          authStore.set((prev) => ({
            ...prev,
            user: userData,
            session: sessionData,
            isAuthenticated: true,
            isLoading: false,
          }));

          // Persist refreshed data (without persisting session tokens)
          _saveLoginData(userData);

          // Sync profile store
          _syncProfileStore(userData);

          // Fetch fresh credits from Supabase in background
          this.fetchCredits();
          this.fetchSubscription();

          // Fetch videos in background
          this.fetchVideos();
        } else {
          // No Supabase session — ensure stale auth cache is cleared.
          _clearLocalData();
          _clearState();
          authStore.setKey('isLoading', false);
        }
      } catch (supabaseErr) {
        console.warn('[authService] Supabase session check failed:', supabaseErr.message);
        authStore.setKey('isLoading', false);
      }
    } catch (err) {
      console.error('[authService] init error:', err);
      authStore.setKey('isLoading', false);
    } finally {
      // Always mark initialized so AuthGate can render
      authStore.setKey('isInitialized', true);
    }

    // Listen for Supabase auth state changes
    try {
      supabaseAuth.onAuthStateChange((event, session) => {
        if (event === 'SIGNED_OUT') {
          libraryService.stopPolling();
          _clearLocalCaches();
          _clearBrowserCaches();
          _clearState();
        }
        if (event === 'TOKEN_REFRESHED' && session) {
          const sessionData = _buildSession(session);
          authStore.setKey('session', sessionData);
        }
      });
    } catch (err) {
      console.warn('[authService] Could not setup auth listener');
    }
  },

  // ───────── Sign In ─────────

  /**
   * Login with email/password.
   * On success: saves to localStorage, fetches credits & videos from Supabase.
   */
  async login(email, password) {
    try {
      authStore.set((prev) => ({ ...prev, isLoading: true, error: null }));

      // Block demo/test/fake email patterns
      if (_isBlockedEmail(email)) {
        throw new Error('This email address is not allowed. Please use a real email.');
      }

      const { session, user } = await supabaseAuth.signIn(email, password);
      const userData = _buildUser(user);
      const sessionData = _buildSession(session);

      _primeLibraryInitialLoading();

      authStore.set((prev) => ({
        ...prev,
        user: userData,
        session: sessionData,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      }));

      // Fetch credits from Supabase
      const credits = await this.fetchCredits();
      await this.fetchSubscription();

      // Persist everything locally
      _saveLoginData(userData);

      // Sync profile store
      _syncProfileStore(userData, credits);

      // Fetch videos in background (don't block login)
      this.fetchVideos();

      router.navigate(ROUTES.HOME);
      return { success: true };
    } catch (error) {
      const message = _translateError(error);
      authStore.set((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
      return { success: false, error: message };
    }
  },

  // ───────── Sign Up ─────────

  /**
   * Register a new user.
   * On success: saves to localStorage, fetches initial credits.
   */
  async register(email, password, metadata = {}, { navigateOnSuccess = true } = {}) {
    try {
      authStore.set((prev) => ({ ...prev, isLoading: true, error: null }));

      // Block demo/test/fake email patterns
      if (_isBlockedEmail(email)) {
        throw new Error('This email address is not allowed. Please use a real email.');
      }

      const data = await supabaseAuth.signUp(email, password, metadata);

      // If session comes back immediately (no email verification required)
      if (data.session && data.user) {
        const userData = _buildUser(data.user, metadata.name);
        const sessionData = _buildSession(data.session);

        _primeLibraryInitialLoading();

        authStore.set((prev) => ({
          ...prev,
          user: userData,
          session: sessionData,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        }));

        // New users get 10 credits via signup trigger — fetch them
        const credits = await this.fetchCredits();
        await this.fetchSubscription();
        _saveLoginData(userData);
        _syncProfileStore(userData, credits);

        if (navigateOnSuccess) {
          router.navigate(ROUTES.HOME);
        }
        return { success: true };
      }

      // Email verification required — user is not yet logged in
      authStore.setKey('isLoading', false);
      return { success: true, needsVerification: true };
    } catch (error) {
      const message = _translateError(error);
      authStore.set((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
      return { success: false, error: message };
    }
  },

  // ───────── Sign Out ─────────

  /**
   * Logout — clears localStorage, resets store, signs out from Supabase.
   */
  async logout() {
    try {
      authStore.setKey('isLoading', true);
      libraryService.stopPolling();
      _clearLocalCaches();
      _clearState();
      await supabaseAuth.signOut();
      await _clearBrowserCaches();
    } catch (err) {
      console.error('[authService] logout error:', err);
    } finally {
      authStore.setKey('isLoading', false);
      router.navigate(ROUTES.HOME);
    }
  },

  // ───────── Forgot Password ─────────

  /**
   * Send password reset email.
   */
  async forgotPassword(email) {
    try {
      authStore.set((prev) => ({ ...prev, isLoading: true, error: null }));
      await supabaseAuth.resetPassword(email);
      authStore.setKey('isLoading', false);
      return { success: true };
    } catch (error) {
      const message = _translateError(error);
      authStore.set((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
      return { success: false, error: message };
    }
  },

  // ───────── Credits (Cloudflare Workers API) ─────────

  /**
   * Fetch user's credit balance via Worker API.
   * Uses the same Cloudflare Worker as the mobile app.
   * @returns {number} credit balance
   */
  async fetchCredits() {
    try {
      authStore.setKey('isCreditsLoading', true);

      const { isAuthenticated } = authStore.get();
      if (!isAuthenticated) {
        authStore.setKey('isCreditsLoading', false);
        return 0;
      }

      const balanceResponse = await creditsApi.getBalance();
      const balance = _extractBalance(balanceResponse);

      authStore.setKey('credits', balance);
      authStore.setKey('isCreditsLoading', false);

      // Update profile store too
      profileStore.set((prev) => ({
        ...prev,
        stats: { ...prev.stats, creditsRemaining: balance },
      }));

      return balance;
    } catch (err) {
      console.warn('[authService] fetchCredits error:', err.message);
      
      // Fallback 1: read cached total_credits from profiles table.
      const fromProfile = await _fetchCreditsFromProfile();
      if (Number.isFinite(fromProfile)) {
        authStore.setKey('credits', fromProfile);
        authStore.setKey('isCreditsLoading', false);
        return fromProfile;
      }

      // Do not fallback to local cache to avoid stale balances.
      authStore.setKey('credits', 0);
      authStore.setKey('isCreditsLoading', false);
      return 0;
    }
  },

  // ───────── Videos (background fetch after login) ─────────

  /**
   * Fetch user's videos from Supabase and save to local storage + library store.
   */
  async fetchVideos() {
    try {
      await libraryService.fetchVideos();
      const { videos } = libraryStore.get();
      localStorageService.set(STORAGE_KEYS.VIDEOS, videos);

      // Update profile stats
      profileStore.set((prev) => ({
        ...prev,
        stats: { ...prev.stats, totalVideos: videos.length },
      }));
    } catch (err) {
      console.error('[authService] fetchVideos error:', err);
    }
  },

  // ───────── Profile (from Supabase) ─────────

  /**
   * Fetch user profile from Supabase profiles table.
   */
  async fetchProfile() {
    try {
      const { user } = authStore.get();
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        console.warn('[authService] fetchProfile error:', error.message);
        return null;
      }

      return data;
    } catch (err) {
      console.error('[authService] fetchProfile error:', err);
      return null;
    }
  },

  /**
   * Fetch user subscription info from Supabase.
   */
  async fetchSubscription() {
    try {
      const { user } = authStore.get();
      if (!user?.id) {
        authStore.setKey('subscription', null);
        profileStore.setKey('subscription', null);
        return null;
      }

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 = no rows found (user has no subscription)
        console.warn('[authService] fetchSubscription error:', error.message);
      }

      const subscription = data || null;
      authStore.setKey('subscription', subscription);
      profileStore.setKey('subscription', subscription);
      return subscription;
    } catch (err) {
      console.error('[authService] fetchSubscription error:', err);
      authStore.setKey('subscription', null);
      profileStore.setKey('subscription', null);
      return null;
    }
  },
};

// ───────── Private Helpers ─────────

/**
 * Build a normalized user object from Supabase user.
 */
function _buildUser(supabaseUser, fallbackName) {
  return {
    id: supabaseUser.id,
    email: supabaseUser.email || '',
    fullName: supabaseUser.user_metadata?.full_name || supabaseUser.user_metadata?.name || fallbackName || '',
    avatarUrl: supabaseUser.user_metadata?.avatar_url || null,
    createdAt: supabaseUser.created_at || null,
  };
}

/**
 * Build a normalized session object from Supabase session.
 */
function _buildSession(supabaseSession) {
  return {
    accessToken: supabaseSession.access_token,
    refreshToken: supabaseSession.refresh_token,
    expiresAt: supabaseSession.expires_at ?? 0,
  };
}

/**
 * Persist login data to localStorage.
 */
function _saveLoginData(user) {
  localStorageService.set(STORAGE_KEYS.AUTH_USER, user);
}

/**
 * Clear all auth data from localStorage.
 */
function _clearLocalData() {
  localStorageService.remove(STORAGE_KEYS.AUTH_USER);
  localStorageService.remove(STORAGE_KEYS.VIDEOS);
}

/**
 * Clear all client-side persisted app/session data.
 */
function _clearLocalCaches() {
  const preservedLocale = localStorageService.get(STORAGE_KEYS.APP_LOCALE, null);
  _clearLocalData();
  localStorageService.clear();
  if (preservedLocale) {
    localStorageService.set(STORAGE_KEYS.APP_LOCALE, preservedLocale);
  }
  sessionStorageService.clear();
}

/**
 * Force-clear Supabase SDK's own persisted session.
 * The SDK stores its session in localStorage under `sb-{ref}-auth-token`.
 * signOut() clears it properly, but we wrap in try/catch as a safety net.
 */
async function _clearSupabaseSession() {
  try {
    await supabaseAuth.signOut();
  } catch (err) {
    console.warn('[authService] Force sign-out failed, clearing SDK storage manually');
    // Fallback: manually remove Supabase SDK session keys
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
        localStorage.removeItem(key);
      }
    }
  }
}

/**
 * Reset store to guest state.
 */
function _clearState() {
  authStore.set((prev) => ({
    user: null,
    session: null,
    subscription: null,
    credits: 0,
    isAuthenticated: false,
    isLoading: false,
    isCreditsLoading: false,
    isInitialized: prev.isInitialized,
    error: null,
  }));
  // Clear full library state (prevents stale loading spinner after session switch)
  libraryStore.set({
    videos: [],
    filter: 'newest',
    isLoading: false,
    hasLoadedOnce: false,
    error: null,
    pagination: {
      cursor: null,
      hasMore: true,
    },
  });
  // Clear profile store
  profileStore.set({
    user: null,
    subscription: null,
    stats: { totalVideos: 0, creditsRemaining: 0 },
    isLoading: false,
    error: null,
  });
}

function _primeLibraryInitialLoading() {
  libraryStore.set((prev) => ({
    ...prev,
    videos: [],
    isLoading: true,
    hasLoadedOnce: false,
    error: null,
    pagination: {
      cursor: null,
      hasMore: true,
    },
  }));
}

/**
 * Clear browser Cache Storage entries (best effort).
 */
async function _clearBrowserCaches() {
  try {
    if (typeof caches === 'undefined') return;
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
  } catch (err) {
    console.warn('[authService] Could not clear browser caches:', err?.message || err);
  }
}

/**
 * Sync profile store with user data
 */
function _syncProfileStore(userData, credits = 0) {
  profileStore.set((prev) => ({
    ...prev,
    user: {
      name: userData.fullName || userData.email?.split('@')[0] || 'User',
      email: userData.email,
      avatarUrl: userData.avatarUrl,
      createdAt: userData.createdAt,
    },
    stats: {
      ...prev.stats,
      creditsRemaining: credits,
    },
  }));
}

/**
 * Check if an email matches any blocked pattern (demo, test, fake, etc.)
 * @param {string} email
 * @returns {boolean}
 */
function _isBlockedEmail(email) {
  if (!email) return false;
  const normalized = email.trim().toLowerCase();
  return BLOCKED_EMAIL_PATTERNS.some((pattern) => pattern.test(normalized));
}

/**
 * Translate Supabase error messages to user-friendly text
 */
function _translateError(error) {
  const msg = error?.message || '';
  
  if (msg.includes('Invalid login credentials')) {
    return 'Invalid email or password. Please try again.';
  }
  if (msg.includes('Email not confirmed')) {
    return 'Please verify your email before logging in.';
  }
  if (msg.includes('User already registered')) {
    return 'An account with this email already exists.';
  }
  if (msg.includes('Password should be at least')) {
    return 'Password must be at least 6 characters.';
  }
  if (msg.includes('rate limit') || msg.includes('too many requests')) {
    return 'Too many attempts. Please wait a moment and try again.';
  }
  if (msg.includes('network') || msg.includes('fetch')) {
    return 'Network error. Please check your connection.';
  }

  return msg || 'An unexpected error occurred. Please try again.';
}

/**
 * Extract balance from different API payload shapes.
 * Supports:
 * - { balance: number }
 * - { data: { balance: number } }
 */
function _extractBalance(payload) {
  const direct = Number(payload?.balance);
  if (Number.isFinite(direct)) return direct;

  const nested = Number(payload?.data?.balance);
  if (Number.isFinite(nested)) return nested;

  return 0;
}

/**
 * Fallback credits fetch from profiles.total_credits.
 */
async function _fetchCreditsFromProfile() {
  try {
    const { user } = authStore.get();
    if (!user?.id) return null;

    const { data, error } = await supabase
      .from('profiles')
      .select('total_credits')
      .eq('id', user.id)
      .maybeSingle();

    if (error) return null;

    const value = Number(data?.total_credits);
    return Number.isFinite(value) ? value : 0;
  } catch {
    return null;
  }
}
